import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { QueryOptions } from '@orderna/admin-panel/src/types/query-options';
import { BehaviorSubject, firstValueFrom, map, Observable } from 'rxjs';
import { IDiscountsApiService } from './discounts-api.interface';

import { environment } from '@orderna/admin-panel/src/environments/environment';
import { Maybe } from '@orderna/admin-panel/src/types/maybe';
import { Data } from '@orderna/admin-panel/src/utils/service';
import {
  DiscountDetail,
  IDiscountCreateData,
  IDiscountDetail,
  IDiscountSummary,
  IDiscountUpdateData,
} from '../../model/discount';
import { Customer } from '../../model/customer';
import { ApplicableDiscount } from '../../model/discount-applicable';
import { Store } from '../../model/store';
import { SuccessResponse } from '../shared/success-response';
import { DiscountApiMapper } from './discount-api.mapper';
import { DiscountFormData } from '../../model/discount-form-data';
import { DiscountStatus } from '../../model/enum/discount-status';
import { DiscountDetails } from '../../model/discount-details';
import { FindDiscountsQueryDto } from './dtos/find-discounts.query.dto';
import { FindDiscountsResponseDto } from './dtos/find-discounts.response.dto';
import { HandleBlobHttpResponseOptions } from '@orderna/admin-panel/src/types/handle-blob-http-response-options';
import { handleBlobHttpResponse } from '@orderna/admin-panel/src/utils/handle-blob-http-response';
import { ExportDiscountsQueryDto } from './dtos/export-discounts.query.dto';
import { ExportExcelPdfService } from '../export-excel-pdf/export-excel-pdf.service';

const API_URL = `${environment.api}/discounts`;
@Injectable({
  providedIn: 'root',
})
export class DiscountsApiService implements IDiscountsApiService {
  private http = inject(HttpClient);
  private exportService = inject(ExportExcelPdfService);

  data = {
    discounts: {
      items: [],
      queryOptions: { page: 1, size: 10 } as QueryOptions<IDiscountDetail>,
      subject: new BehaviorSubject<Maybe<DiscountDetail[]>>(null),
      totalSubject: new BehaviorSubject<Maybe<number>>(null),
      totalAfterFilterSubject: new BehaviorSubject<Maybe<number>>(null),
    },
  };

  get discountsData() {
    return this.data['discounts'] as Data<DiscountDetail, IDiscountDetail>;
  }

  createDiscount(discount: IDiscountCreateData): Observable<any> {
    return this.http.post(`${API_URL}`, {
      ...discount,
      status: DiscountStatus.ACTIVE,
    });
  }

  updateDiscount(
    discount_id: any,
    discount: IDiscountUpdateData
  ): Observable<any> {
    const { isArchived, ...rest } = discount;

    const status = typeof isArchived === 'boolean' && {
      status: isArchived ? DiscountStatus.ARCHIVED : DiscountStatus.ACTIVE,
    };

    return this.http.patch(`${API_URL}/${discount_id}`, {
      ...status,
      ...rest,
    });
  }

  deleteDiscount(discountId: IDiscountSummary['id']): Observable<any> {
    return this.http.delete(`${API_URL}/${discountId}`, { body: {} });
  }

  deleteDiscounts(discountsIds: IDiscountSummary['id'][]): Observable<any> {
    let params = new HttpParams();
    discountsIds.forEach((id) => {
      params = params.append('ids', id.toString());
    });

    return this.http.delete(`${API_URL}/delete-many`, {
      params,
      body: {},
    });
  }

  findDiscountFormData(discountId: number): Observable<DiscountFormData> {
    return this.http
      .get<SuccessResponse>(`${API_URL}/${discountId}`)
      .pipe(
        map((response) => DiscountApiMapper.toDiscountFormData(response.data))
      );
  }

  async findDiscounts(
    query: FindDiscountsQueryDto
  ): Promise<FindDiscountsResponseDto> {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindDiscountsResponseDto>>(
        `${API_URL}`,
        {
          params,
        }
      )
    );

    return response.data;
  }

  async exportDiscounts(
    query: ExportDiscountsQueryDto,
    options?: HandleBlobHttpResponseOptions
  ) {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get(`${API_URL}/export`, {
        responseType: 'blob',
        observe: 'response',
        params,
      })
    );

    handleBlobHttpResponse(response, options, this.exportService);
  }

  getApplicableDiscounts(
    storeId: Store['id'],
    amount: number,
    customerId?: Customer['id']
  ): Observable<ApplicableDiscount[]> {
    let params = new HttpParams().set('storeId', storeId).set('amount', amount);

    if (customerId) {
      params = params.set('customerId', customerId);
    }

    return this.http
      .get<SuccessResponse>(`${API_URL}/applicable`, { params })
      .pipe(map((response) => response.data as ApplicableDiscount[]));
  }

  getDiscountDetails(discountId: number): Observable<DiscountDetails> {
    return this.http
      .get<SuccessResponse>(`${API_URL}/${discountId}/details`)
      .pipe(map((response) => DiscountApiMapper.toDiscountDetails(response.data)));
  }
}
